import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, BookOpen } from "lucide-react";
import { BrandMark, BrandWordmark } from "./LandingPage";
import SiteFootnote from "../components/SiteFootnote";
import EntityFooter from "../components/EntityFooter";
import { LEARN_CONCEPTS, LEARN_CATEGORIES } from "../lib/learnConcepts";
import {
  setStandardMeta,
  upsertJsonLd,
  buildOrganizationSchema,
  buildBreadcrumbSchema,
  ORG,
} from "../lib/seo";
import Breadcrumb from "../components/Breadcrumb";

export default function LearnHubPage() {
  useEffect(() => {
    const url = `${window.location.origin}/learn`;
    const title = "Financial Ratios Glossary — CFO Grade";
    const desc =
      "Plain-English explainers for every financial metric CFO Grade computes — EBITDA, DSCR, current ratio, working capital, and 20+ more. Each with formulas, benchmarks, and worked examples.";
    setStandardMeta({ title, description: desc, url });

    // CollectionPage + DefinedTermSet + BreadcrumbList wraps every concept
    // under one glossary
    upsertJsonLd("learn-hub-graph", {
      "@context": "https://schema.org",
      "@graph": [
        buildOrganizationSchema(),
        {
          "@type": "CollectionPage",
          "@id": `${url}#page`,
          name: title,
          description: desc,
          url,
          isPartOf: { "@id": `${ORG.url}#org` },
        },
        {
          "@type": "DefinedTermSet",
          name: "CFO Grade Financial Ratios Glossary",
          url,
          description: desc,
          hasDefinedTerm: LEARN_CONCEPTS.map((c) => ({
            "@type": "DefinedTerm",
            name: c.title,
            url: `${ORG.url}/learn/${c.slug}`,
            termCode: c.slug,
          })),
        },
        buildBreadcrumbSchema([
          { name: "Home", url: ORG.url },
          { name: "Glossary", url },
        ]),
      ],
    });
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const cat of LEARN_CATEGORIES) map[cat.key] = [];
    for (const c of LEARN_CONCEPTS) {
      if (!map[c.category]) map[c.category] = [];
      map[c.category].push(c);
    }
    return map;
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain" data-testid="learn-hub-page">
      <header className="max-w-6xl mx-auto px-6 sm:px-10 py-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <BrandWordmark size="sm" />
        </Link>
        <Link
          to="/"
          className="text-sm text-[#5C665D] hover:text-[#1F382E] inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to home
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 pt-6 pb-20">
        <Breadcrumb
          items={[
            { name: "Home", to: "/" },
            { name: "Glossary", to: "/learn" },
          ]}
        />
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#5C665D] bg-white border border-[#E5E0D8] rounded-full px-3 py-1.5 mt-4">
          <BookOpen size={13} strokeWidth={1.5} /> Glossary
        </div>

        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1F382E] leading-[1.05] tracking-tight mt-5 max-w-3xl">
          Every metric, in plain English.
        </h1>

        <p className="text-base sm:text-lg text-[#1A1A1A] leading-relaxed mt-6 max-w-2xl">
          Short, sharp explainers for the {LEARN_CONCEPTS.length} financial concepts CFO Grade computes —
          with formulas, industry benchmarks, and worked examples. No jargon, no fluff.
        </p>

        <div className="mt-12 space-y-14">
          {LEARN_CATEGORIES.map((cat) => {
            const items = grouped[cat.key] || [];
            if (items.length === 0) return null;
            return (
              <section key={cat.key} data-testid={`learn-section-${cat.key}`}>
                <h2 className="font-serif-display text-2xl text-[#1F382E] tracking-tight">
                  {cat.label}
                </h2>
                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/learn/${c.slug}`}
                      className="group block bg-white border border-[#E5E0D8] hover:border-[#1F382E]/30 rounded-2xl p-5 transition-all duration-300 hover:shadow-sm"
                      data-testid={`learn-card-${c.slug}`}
                    >
                      <p className="font-serif-display text-lg text-[#1F382E] tracking-tight group-hover:text-[#C26D5C] transition-colors">
                        {c.title}
                      </p>
                      <p className="text-sm text-[#5C665D] leading-relaxed mt-2 line-clamp-3">
                        {c.short}
                      </p>
                      <span className="inline-flex items-center gap-1 text-xs text-[#1F382E] mt-3 font-medium">
                        Read explainer
                        <ArrowRight
                          size={12}
                          strokeWidth={1.5}
                          className="group-hover:translate-x-0.5 transition-transform"
                        />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <div className="mt-20 bg-white border border-[#E5E0D8] rounded-2xl p-7 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <p className="font-serif-display text-2xl text-[#1F382E] tracking-tight">
              Stop reading. Start grading.
            </p>
            <p className="text-sm text-[#5C665D] mt-2 max-w-md">
              Drop your numbers in and CFO Grade computes all {LEARN_CONCEPTS.length}+ of these in seconds,
              with your industry's benchmarks already loaded.
            </p>
          </div>
          <Link to="/analyze">
            <button
              className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-11 px-6 transition-all duration-300 inline-flex items-center gap-2"
              data-testid="learn-hub-cta-analyze"
            >
              Run a free analysis <ArrowRight size={14} strokeWidth={1.5} />
            </button>
          </Link>
        </div>

        <EntityFooter />
      </main>

      <SiteFootnote />
    </div>
  );
}
